import { useState, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faTags,
  faSave,
  faUsers,
  faUserCheck,
  faUserSlash,
  faSpinner,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import {
  statusStyles,
  statusDotStyles,
  formatDateTime,
} from "../../../utils/styles";
import Modal from "../../../components/ui/modal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { Input } from "../../../components/ui/input";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const ProductType = ({
  productTypes,
  productTypesLoading,
  productTypesError,
  productTypesPagination,
  productTypesPage,
  setProductTypesPage,
  productTypesPageSize,
  setProductTypesPageSize,
  productTypesSearch,
  setProductTypesSearch,
  createProductType,
  updateProductType,
  toggleProductTypeStatus,
  refreshProductTypes,
}) => {
  const { hasPermission } = useAuth();
  const canManageType = hasPermission([
    "manage_product_type",
    "manage_product",
  ]);

  const safeProductTypes = useMemo(
    () => (Array.isArray(productTypes) ? productTypes : []),
    [productTypes],
  );

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({ name: "", status: "active" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredTypes = useMemo(() => {
    let result = safeProductTypes;
    if (statusFilter !== "all") {
      result = result.filter(
        (type) => type.status?.toLowerCase() === statusFilter,
      );
    }
    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((t) => {
        const createdDate = new Date(t.created_at);
        return createdDate >= start && createdDate <= end;
      });
    }
    return result;
  }, [safeProductTypes, statusFilter, startDate, endDate, isFilterApplied]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    if (!canManageType) return;
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Type name is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      setIsSubmitting(true);
      const data = { name: form.name, status: form.status };
      if (editingType) {
        await updateProductType(editingType.productTypeId, data);
        toastSuccess("Product type updated successfully!");
      } else {
        await createProductType(data);
        toastSuccess("Product type created successfully!");
      }
      setDialogOpen(false);
      setEditingType(null);
      setForm({ name: "", status: "active" });
      setErrors({});
      await refreshProductTypes();
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to save product type",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (productTypeId, currentStatus) => {
    if (!canManageType) return;
    try {
      await toggleProductTypeStatus(productTypeId);
      toastSuccess(
        `Product type ${currentStatus === "active" ? "deactivated" : "activated"} successfully!`,
      );
      await refreshProductTypes();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update status");
    }
  };

  const statusTabs = [
    { id: "all", label: "All", icon: faUsers },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const tabCounts = {
    all: productTypesPagination?.total ?? safeProductTypes.length,
    active: safeProductTypes.filter(
      (t) => t.status === "active" || t.status === "ACTIVE",
    ).length,
    inactive: safeProductTypes.filter(
      (t) => t.status === "inactive" || t.status === "INACTIVE",
    ).length,
  };

  const totalItems = productTypesPagination?.total ?? safeProductTypes.length;
  const totalPages = productTypesPagination?.last_page ?? 1;

  if (productTypesLoading && safeProductTypes.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading product types...</p>
        </div>
      </div>
    );
  }

  if (productTypesError) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-600">
          Failed to load product types. Please try again.
        </p>
        <Button
          onClick={() => refreshProductTypes()}
          className="ml-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Product Types</h3>
          <p className="text-sm text-gray-600">
            Manage product types ({totalItems} total)
          </p>
        </div>
        {canManageType && (
          <Button
            onClick={() => {
              setEditingType(null);
              setForm({ name: "", status: "active" });
              setErrors({});
              setDialogOpen(true);
            }}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add type
          </Button>
        )}
      </div>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setProductTypesPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setProductTypesPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredTypes.length}
        label="product types"
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={productTypesSearch}
          onChange={(value) => {
            setProductTypesSearch(value);
            setProductTypesPage(1);
          }}
          placeholder="Search by type name..."
          className="flex-1"
        />

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setProductTypesPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                statusFilter === tab.id
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
              <span>{tab.label}</span>
              <span
                className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                  statusFilter === tab.id
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {tabCounts[tab.id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <TableWrapper
        title="items"
        totalItems={totalItems}
        currentPage={productTypesPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Type Name
              </TableHead>

              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Created At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Updated At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTypes.length > 0 ? (
              filteredTypes.map((item, i) => {
                const status = item.status?.toUpperCase() || "ACTIVE";
                const typeId = item.productTypeId ?? item.id;
                const rowNumber =
                  (productTypesPage - 1) * productTypesPageSize + i + 1;
                return (
                  <TableRow
                    key={typeId ?? i}
                    className="hover:bg-blue-50/50 transition-colors border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {rowNumber}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {item.name}
                    </TableCell>

                    <TableCell className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(item.created_at)}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(item.updated_at)}
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[status] || "bg-gray-100 text-gray-700"} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[status] || "bg-gray-400"}`}
                        />
                        {status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        {canManageType ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingType(item);
                                setForm({
                                  name: item.name,
                                  status: item.status || "active",
                                });
                                setErrors({});
                                setDialogOpen(true);
                              }}
                              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3"
                              />{" "}
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleToggleStatus(typeId, item.status)
                              }
                              className={`gap-1 ${
                                item.status === "active" ||
                                item.status === "ACTIVE"
                                  ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                  : "border-green-500 text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  item.status === "active" ||
                                  item.status === "ACTIVE"
                                    ? faToggleOff
                                    : faToggleOn
                                }
                                className="h-3 w-3"
                              />
                              {item.status === "active" ||
                              item.status === "ACTIVE"
                                ? "Deactivate"
                                : "Activate"}
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <EmptyState
                    title="No product types found"
                    message={
                      productTypesSearch || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "Add your first product type to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={productTypesPage}
          totalPages={totalPages}
          pageSize={productTypesPageSize}
          totalItems={totalItems}
          onPageChange={setProductTypesPage}
          onPageSizeChange={setProductTypesPageSize}
          pageSizeOptions={[5, 10, 25, 50, 100, 250]}
        />
      )}

      <Modal
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingType(null);
          setErrors({});
        }}
        title={editingType ? "Edit product type" : "Add new product type"}
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setEditingType(null);
                setErrors({});
              }}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
              className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-4 w-4 animate-spin"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  {editingType ? "Update" : "Save"}
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faTags}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold">
                {editingType ? "Edit product type" : "Create new product type"}
              </p>
              <p className="text-xs text-gray-600">
                Fill in all required fields (*)
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faTags}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Type name *
            </label>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter type name"
              disabled={isSubmitting}
              className={`border ${errors.name ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium">Status</label>
            <Select
              value={form.status}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, status: value }))
              }
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full h-10 border-gray-300">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProductType;
